import * as Yup from 'yup';
import isEmailValidator, { IsEmailOptions } from 'validator/lib/isEmail';
import { isValidFileType } from '../../utils/global/helper';
import { MAX_FILE_SIZE } from '../../constants/global';

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export const getUrlSchema = (error: string) =>
  Yup.string()
    .transform(currentValue => {
      const doesNotStartWithHttp =
        typeof currentValue === 'string' &&
        currentValue.trim() &&
        !(
          currentValue.startsWith('http://') ||
          currentValue.startsWith('https://')
        );

      if (doesNotStartWithHttp) {
        return `https://${currentValue}`;
      }

      return currentValue;
    })
    .url(error);

export const getEmailSchema = (
  error: string,
  required = true,
  options?: IsEmailOptions,
) =>
  Yup.string()
    .test({
      test: value => {
        if (!required && !value) return true;
        return typeof value === 'string' && isEmailValidator(value, options);
      },
      message: error,
    })
    .typeError(error);

export const getFileSchema = (acceptedFileTypes: string[]) =>
  Yup.mixed<File>()
    .test({
      name: 'file-type',
      message: 'Please provide a supported file type',
      test: (file: any, context) => {
        const isValid =
          !!file &&
          file instanceof File &&
          isValidFileType(file, acceptedFileTypes);
        if (!isValid) context?.createError();
        return isValid;
      },
    })
    .test({
      name: 'file-size',
      message: `File too big, can't exceed ${MAX_FILE_SIZE / 1024 / 1024}MB`,
      test: (file: any, context) => {
        const isValid = file instanceof File && file?.size < MAX_FILE_SIZE;
        if (!isValid) context?.createError();
        return isValid;
      },
    });

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

export const Mediums = {
  EMAIL: 'email',
  SMS: 'sms',
} as const;

export type Medium = (typeof Mediums)[keyof typeof Mediums];

export interface CustomerMessagePayload {
  mediums: Medium[];
  email?: string | null;
  phone?: string | null;
}

export const getMediumSchema = ({
  requireEmail,
  requirePhone,
}: { requireEmail?: boolean; requirePhone?: boolean } = {}) =>
  Yup.object({
    mediums: Yup.array()
      .of(Yup.string().oneOf(Object.values(Mediums)))
      .min(1, 'At least one method is required')
      .required('Methods are required'),
    email: requireEmail
      ? getEmailSchema('Email is required').when('mediums', {
          is: (mediums: Medium[]) => mediums.includes(Mediums.EMAIL),
          then: schema => schema,
          otherwise: () => Yup.string().nullable().optional(),
        })
      : Yup.string().nullable().optional(),
    phone: requirePhone
      ? Yup.string().when('mediums', {
          is: (mediums: Medium[]) => mediums.includes(Mediums.SMS),
          then: schema => schema.required('Phone is required'),
          otherwise: schema => schema.nullable().optional(),
        })
      : Yup.string().nullable().optional(),
  });
